import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '@/app/core/auth/auth.service';
import { ActiveCompanyService } from '@/app/core/company/active-company.service';
import { PermissionsService } from '@/app/core/permissions/permissions.service';

// Sektörel modül route → gerekli policy listesi (OR mantığı).
// Map order önemli: daha spesifik path ('erp/packages') önce, generic ('erp') sonra.
// Faz 2 sektör-agnostik ekranlar (BOM, commissions, packages, giftcards) hem
// ERP rolünden hem de herhangi bir sektörel modül rolünden erişilebilmeli —
// backend `RequireFazIkiPolicy` attribute'üyle hizalı.
const SECTOR_OR_ERP_VIEW = [
  'erp.view',
  'beauty.view', 'fitness.view', 'hotel.view', 'fnb.view',
  'education.view', 'health.view', 'law.view', 'realestate.view',
];

const ROUTE_POLICY_MAP: Record<string, string[]> = {
  'erp/packages':    SECTOR_OR_ERP_VIEW,
  'erp/giftcards':   SECTOR_OR_ERP_VIEW,
  'erp/commissions': SECTOR_OR_ERP_VIEW,
  'erp/bom':         SECTOR_OR_ERP_VIEW,

  'booking':     ['booking.view'],
  'health':      ['health.view'],
  'law':         ['law.view'],
  'hotel':       ['hotel.view'],
  'beauty':      ['beauty.view'],
  'education':   ['education.view'],
  'fitness':     ['fitness.view'],
  'fnb':         ['fnb.view'],
  'realestate':  ['realestate.view'],
  'crm':         ['crm.view'],
  'erp':         ['erp.view'],
  // Mağaza: iki policy'den biri yeterli. 'erp.view' de kabul ediliyor çünkü
  // ekranlar ERP uçlarını okuyor ve ERP modülü olan kiracı mağazayı da görebilir.
  'ecommerce':   ['ecommerce.view', 'erp.view'],
  'web-builder': ['webbuilder.view'],

  // Melbear zamanlanmış görevler — gözetimsiz yazma demek, kendi kapısı var.
  'melbear/schedules': ['ai.agent.schedule'],

  // Sosyal Medya Asistanı. Hesap bağlama, gönderi ve gelen kutusu AYRI
  // yetkiler; her sayfa kendi kapısını ister.
  'social/accounts': ['social.account.manage'],
  'social/posts':    ['social.post.create'],
  // Takvim zamanlama demek; zamanlamak yayınlamaktır, yalnız ertelenmiştir.
  'social/calendar':  ['social.publish'],
  'social/approvals': ['social.post.approve'],
  'social/inbox':    ['social.inbox.read'],
  'social':          ['social.post.create'],

  // Veri dışa aktarımı — kiracının TÜM verisi tek pakette; modül görüntüleme
  // yetkisi yetmez, kendi kapısı var. Sahip zaten bypass eder.
  'settings/data': ['client.data.export'],

  // Finans alt-sayfaları — modül view yetmez, finance.collect gerekir.
  'clinic/billing': ['clinic.finance.collect'],

  // Yeni sektörel modüller — owner bypass'a güvenmek yerine policy bazlı da kontrol.
  'site':          ['site.view'],
  'storage':       ['storage.view'],
  'ngo':           ['ngo.view'],
  'religious':     ['rel.view'],
  'consulting':    ['cons.view'],
  'eventorg':      ['evt.view'],
  'homeservices':  ['hs.view'],
  'construction':  ['cns.view'],
  'realty':        ['realty.view'],
  'agency':        ['agency.view'],
  'agriculture':   ['agriculture.view'],
  'automotive':    ['automotive.view'],
  'creator':       ['creator.view'],
  'daycare':       ['daycare.view'],
  'petservices':   ['petservices.view'],
  // Doz hesabi AYRI izin ailesi: protokol duzenlemek klinik guvenlik isidir,
  // `petservices.update` (randevu/fatura) ile ayni yetkiye baglanmamali.
  'petservices/dose': ['dose.view'],
  // Tazminat PARAYLA ilgilidir; `petservices.view` yetmez.
  'petservices/claims': ['claim.view'],
  // Lisans sarti SOZLESMEdir; `creator.update` (icerik duzenleme) yetmez.
  'creator/deal-terms': ['terms.view'],
  // Gece denetimi her folyoya para yazar; `hotelmgmt.update` yetmez.
  'hotelmgmt/night-audit': ['audit.view'],
  // Veli bildirimi modul-agnostik: TEK politika ailesi, sektor basina ayri
  // kod her yeni modulde birinin unutulmasi demek olurdu.
  'daycare/notices': ['notice.view'],
  'edumgmt/notices': ['notice.view'],
  // Denetim formu modul-agnostik: TEK politika ailesi.
  'construction/inspections': ['checklist.view'],
  // Şantiye denetimi ortak kontrol formundan ayrı ekran: modülün kendi yetkisi.
  'construction/site-inspections': ['construction.view'],
  'restaurant/inspections': ['checklist.view'],
  'hotelmgmt/inspections': ['checklist.view'],
  'automotive/inspections': ['checklist.view'],
  // Ekspertiz, denetim formundan ayrı ekran: modülün kendi yetkisiyle açılır.
  'automotive/appraisals': ['automotive.view'],
  // Karne YAYIMLAMAK veliye soz vermektir; `edumgmt.update` yetmez.
  'edumgmt/report-cards': ['card.view'],
  // Masa oturumu acmak QR'i siparise ACAR; `restaurant.update` yetmez.
  'restaurant/qr': ['qr.view'],
  'restaurant':    ['restaurant.view'],
  'hotelmgmt':     ['hotelmgmt.view'],
  'edumgmt':       ['edumgmt.view'],
  'lawmgmt':       ['lawmgmt.view'],
  'immigration':   ['immigration.view'],
  'clinic':        ['clinic.view'],
  'pharmacy':      ['pharmacy.view'],
  'dental':        ['dental.view'],
  'logistics':     ['logistics.view'],
  'sportsclub':    ['sportsclub.view'],
};

// Route prefix → aktif şirkette yüklü olması beklenen modül kodu
// (PortalPermissionsManager.ActiveCompanyModules = ClientModule.ModuleCode).
// Owner her policy'i bypass eder; modül gerçekten aktif değilse menü
// gizli olsa da elle URL ile sayfaya girilmemeli (örn. Bloor Walk-in Clinic
// yüklü, kullanıcı /portal/fitness/check-in yazıyor → engelle).
//
// NOT — modül kodu ≠ route prefix olan istisnalar:
//   - 'web-builder' route → modül kodu 'webbuilder'
//   - ERP Faz 2 sektör-agnostik ekranları (erp/packages, erp/giftcards,
//     erp/commissions, erp/bom) KASITEN burada YOK — onlar beauty/fitness
//     gibi sektör kullanıcılarına da açık (ROUTE_POLICY_MAP'teki
//     SECTOR_OR_ERP_VIEW ile korunur). Sadece saf ERP ekranları 'erp'
//     modülünü ister.
const ROUTE_MODULE_MAP: Record<string, string> = {
  beauty:       'beauty',
  fitness:      'fitness',
  hotel:        'hotel',
  fnb:          'fnb',
  education:    'education',
  health:       'health',
  law:          'law',
  realestate:   'realestate',
  site:         'site',
  storage:      'storage',
  ngo:          'ngo',
  religious:    'religious',
  consulting:   'consulting',
  eventorg:     'eventorg',
  homeservices: 'homeservices',
  construction: 'construction',
  realty:       'realty',
  agency:       'agency',
  agriculture:  'agriculture',
  automotive:   'automotive',
  creator:      'creator',
  daycare:      'daycare',
  petservices:  'petservices',
  restaurant:   'restaurant',
  hotelmgmt:    'hotelmgmt',
  edumgmt:      'edumgmt',
  lawmgmt:      'lawmgmt',
  immigration:  'immigration',
  clinic:       'clinic',
  pharmacy:     'pharmacy',
  dental:       'dental',
  logistics:    'logistics',
  sportsclub:   'sportsclub',

  // Çekirdek/yatay modüller — yüklü değilse sayfaları da kapalı.
  crm:           'crm',
  booking:       'booking',
  'web-builder': 'webbuilder',

  // Sosyal Medya Asistanı satın alınmadıysa sayfalar kapalı. Bu kapı owner'ı
  // da bypass ettirmez — asistan ayrı bir abonelik (AI_SOCIAL_ASSIST).
  social: 'social',

  // Mağaza ekranları 'ecommerce' modülünü ister. Bu kapı owner'ı da bypass
  // ettirmez; e-ticaret kiracısına verilen erp.* policy'leri /portal/erp/*
  // ekranlarını AÇMAZ çünkü orada 'erp' modül kodu aranıyor.
  ecommerce: 'ecommerce',

  // Saf ERP ekranları 'erp' modülünü ister (Faz 2 paylaşımlı ekranlar hariç).
  'erp/products': 'erp',
  'erp/orders':   'erp',
  'erp/invoices': 'erp',
  'erp/stock':    'erp',
  'erp/payments': 'erp',
};

// ─── Admin guard ─────────────────────────────────────────────────────────────
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isAuthenticated()) return true;
  return router.createUrlTree(['/auth/sign-in']);
};

// ─── Guest guard ─────────────────────────────────────────────────────────────
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isAuthenticated()) return router.createUrlTree(['/admin/dashboards/melland']);
  return true;
};

// ─── Portal guard ─────────────────────────────────────────────────────────────
// 1. Login değilse → /auth/sign-in
// 2. Platform admin ise → /admin
// 3. Route'un gerektirdiği policy yoksa → /portal/dashboard (403)
//    Policy kaynağı: PermissionsService (JWT'den bağımsız, dinamik)
export const portalGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/auth/sign-in']);
  }

  if (authService.isPlatformAdmin()) {
    return router.createUrlTree(['/admin/dashboards/melland']);
  }

  // Hedef URL'i state'ten al — parent canActivate'te route.pathFromRoot child
  // segmentlerini içermez; state.url tam hedef yolu verir (query/fragment hariç).
  const fullUrl = state.url.split('?')[0].split('#')[0];

  // Daha spesifik path'i önce eşle (erp/packages → erp/packages, erp/products → erp).
  const matchKey = Object.keys(ROUTE_POLICY_MAP)
    .sort((a, b) => b.length - a.length)
    .find(seg => fullUrl.includes(`/${seg}`));

  // Sektörel modül kontrolü — ROUTE_POLICY_MAP'te değil ROUTE_MODULE_MAP'te
  // anahtar bulunsa bile gating bağımsız çalışmalı.
  const moduleKey = Object.keys(ROUTE_MODULE_MAP)
    .sort((a, b) => b.length - a.length)
    .find(seg => fullUrl.includes(`/${seg}`));

  if (matchKey || moduleKey) {
    const companySvc = inject(ActiveCompanyService);
    const permissionsSvc = inject(PermissionsService);

    // Şirket seçilmemiş → dashboard'a yönlendir
    if (!companySvc.activeCompanyId()) {
      return router.createUrlTree(['/portal/dashboard']);
    }

    // Modül-bazlı kapı: yüklenmemiş sektörel modüle URL ile elle girilmesin.
    // Owner her policy'i bypass eder ama bu kapı kasten owner-agnostic —
    // "şirket bu modülü gerçekten satın aldı mı?" sorusuna cevap verir.
    if (moduleKey && permissionsSvc.loaded()) {
      const moduleCode = ROUTE_MODULE_MAP[moduleKey];
      if (!permissionsSvc.hasModule(moduleCode)) {
        return router.createUrlTree(['/portal/dashboard']);
      }
    }

    // Policy kapısı — owner her policy'i bypass eder ([[feedback_owner_bypass]]);
    // değilse listedeki en az bir policy gerekir, yoksa → dashboard.
    if (matchKey && !permissionsSvc.isOwner()) {
      const requiredPolicies = ROUTE_POLICY_MAP[matchKey];
      if (permissionsSvc.loaded() && !requiredPolicies.some(p => permissionsSvc.hasNow(p))) {
        return router.createUrlTree(['/portal/dashboard']);
      }
    }
  }

  return true;
};

// canActivateChild varyantı — parent `canActivate` yalnız ilk girişte çalışır;
// SPA içi alt-route geçişlerinde modül/policy kapısının çalışması için her
// child navigasyonunda tetiklenmeli. portalGuard ile aynı mantık (imza uyumlu).
export const portalChildGuard: CanActivateChildFn = (childRoute, state) =>
  (portalGuard as CanActivateChildFn)(childRoute, state);
